import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { meverProducts } from '../../../mocks/meverProducts';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import SharedNavigation from '../../../components/feature/SharedNavigation';
import SharedFooter from '../../../components/feature/SharedFooter';

export default function MeverProductDetail() {
  const { productId } = useParams<{ productId: string }>();
  const product = meverProducts.find((p) => p.id === productId);
  const [activeImageIndex] = useState(0);
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.05 });
  const navigate = useNavigate();

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-6">製品が見つかりませんでした。</p>
          <Link
            to="/mever"
            className="text-gray-900 underline cursor-pointer"
          >
            MeVERトップへ戻る
          </Link>
        </div>
      </div>
    );
  }

  const relatedProducts = meverProducts
    .filter((p) => p.id !== product.id)
    .slice(0, 3);

  const smoothScrollTo = (elementId: string) => {
    const el = document.getElementById(elementId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navigateToContact = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('contact');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  return (
    <div className="relative">
      <SharedNavigation subLabel="MeVER" transparent={false} />

      {/* Breadcrumb */}
      <div className="pt-24 pb-4 px-8 lg:px-32 bg-white">
        <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm text-gray-400">
          <Link
            to="/"
            className="hover:text-gray-900 transition-colors cursor-pointer"
          >
            Home
          </Link>
          <span>/</span>
          <Link
            to="/mever"
            className="hover:text-gray-900 transition-colors cursor-pointer"
          >
            MeVER
          </Link>
          <span>/</span>
          <span className="text-gray-700">{product.nameEn}</span>
        </div>
      </div>

      {/* Product Detail */}
      <section
        ref={ref}
        className="py-12 lg:py-20 px-8 lg:px-32 bg-white"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Image */}
            <div
              className={`transition-all duration-700 ${
                isVisible
                  ? 'opacity-100 translate-x-0'
                  : 'opacity-0 -translate-x-10'
              }`}
            >
              <div className="w-full h-[560px] bg-gray-50 rounded-2xl overflow-hidden">
                <img
                  src={product.image}
                  alt={product.nameEn}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Info */}
            <div
              className={`transition-all duration-700 delay-200 ${
                isVisible
                  ? 'opacity-100 translate-x-0'
                  : 'opacity-0 translate-x-10'
              }`}
            >
              <p className="text-xs uppercase tracking-wider text-gray-400 mb-4">
                {product.category}
              </p>
              <h1 className="text-3xl lg:text-4xl font-medium text-gray-900 mb-2">
                {product.nameJa}
              </h1>
              <p className="text-lg text-gray-500 mb-8">{product.nameEn}</p>
              <p className="text-3xl font-medium text-gray-900 mb-10">
                {product.price}
              </p>

              <div className="mb-10">
                <p className="text-base text-gray-700 leading-loose mb-4">
                  {product.descriptionJa}
                </p>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {product.descriptionEn}
                </p>
              </div>

              {/* Features */}
              <div className="mb-10">
                <h3 className="text-sm uppercase tracking-wider text-gray-400 mb-5">
                  Features
                </h3>
                <ul className="space-y-3">
                  {product.features.map((feature, idx) => (
                    <li
                      key={idx}
                      className="flex items-center gap-3 text-sm text-gray-700"
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-check-line text-gray-900"></i>
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA */}
              <div className="flex gap-4">
                <button
                  onClick={() => smoothScrollTo('mever-inquiry')}
                  className="px-10 h-14 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-gray-800 transition-colors flex items-center justify-center cursor-pointer whitespace-nowrap"
                >
                  お問い合わせ / Inquire
                </button>
                <Link
                  to="/mever"
                  className="px-10 h-14 border border-gray-300 text-gray-900 text-sm font-medium rounded-full hover:bg-gray-50 transition-colors flex items-center justify-center cursor-pointer whitespace-nowrap"
                >
                  一覧に戻る / Back
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      <section className="py-20 lg:py-28 px-8 lg:px-32 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-medium text-gray-900 mb-12">
            Other Products / その他の製品
          </h2>
          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            data-product-shop
          >
            {relatedProducts.map((rp) => (
              <Link
                to={`/mever/${rp.id}`}
                key={rp.id}
                className="group bg-white rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer"
              >
                <div className="w-full h-64 overflow-hidden">
                  <img
                    src={rp.image}
                    alt={rp.nameEn}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <p className="text-xs uppercase tracking-wider text-gray-400 mb-2">
                    {rp.category}
                  </p>
                  <h3 className="text-base font-medium text-gray-900 mb-1">
                    {rp.nameJa}
                  </h3>
                  <p className="text-sm text-gray-500">{rp.price}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Section */}
      <section
        id="mever-inquiry"
        className="py-20 lg:py-28 px-8 lg:px-32 bg-white"
      >
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-medium text-gray-900 mb-4">
            Product Inquiry
          </h2>
          <p className="text-sm text-gray-500 mb-6">
            製品に関するお問い合わせ
          </p>
          <p className="text-base text-gray-600 mb-10">
            製品の詳細、法人向けのお見積もり、カスタマイズのご相談など、お気軽にお問い合わせください。
          </p>
          <button
            onClick={navigateToContact}
            className="inline-flex items-center gap-2 px-10 h-14 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            お問い合わせフォームへ / Contact Form
          </button>
        </div>
      </section>

      <SharedFooter />
    </div>
  );
}
